<template>
    <div class="[ flex flex-col gap-4 ]" v-bind="$attrs">
        <div class="[ flex justify-between items-center gap-4 ]">
            <div class="[ flex-1 flex ]">
                <h2 class="[ font-bold ]">Hooks</h2>
            </div>
            <ButtonComponent label="Create hook" outlined @click="openBlankDrawer">
                <template #icon>
                    <JBIcon name="add" />
                </template>
            </ButtonComponent>
        </div>

        <DataTable :value="data" :loading="loading" @row-select="selectRow($event.data)" selection-mode="single">
            <template #empty>
                <Empty icon="unavailable" title="No hooks"
                    message="Create a hook to be notified when an event occurs." />
            </template>
            <Column field="id" header="Id">
                <template #body="{ data }: { data: Hook }">
                    <div>{{ data.id }}</div>
                </template>
            </Column>
            <Column header="Enabled">
                <template #body="{ data }: { data: Hook }">
                    <Checkbox :model-value="data.isEnabled" binary readonly />
                </template>
            </Column>
            <Column field="name" header="Name">
                <template #body="{ data }: { data: Hook }">
                    <div>{{ data.name }}</div>
                </template>
            </Column>
            <Column field="externalServiceName" header="External Service">
                <template #body="{ data }: { data: Hook }">
                    <!-- TODO: Add link to the service, or maybe a drawer to preview it -->
                    <div>{{ data.externalService?.name }}</div>
                </template>
            </Column>
            <Column field="events" header="Events">
                <template #body="{ data }: { data: Hook }">
                    <div class="[ flex flex-wrap gap-2 ]">
                        <Badge v-for="event in data.events" :key="event" :value="event" />
                    </div>
                </template>
            </Column>
            <Column>
                <template #body="{ data }: { data: Hook }">
                    <ButtonComponent outlined label="Delete" @click="confirmDelete(data.id!)" />
                </template>
            </Column>
        </DataTable>
    </div>
    <JBDialog v-model:visible="showConfirm" header="Delete hook">
        <div>Are you sure you want to delete this hook?</div>
        <template #footer>
            <div class="[ flex gap-3 justify-end ]">
                <ButtonComponent label="Delete" @click="commitDelete" :loading="isDeleting" />
            </div>
        </template>
    </JBDialog>
    <JBDrawer v-model="showDrawer" :title="selectedId === -1 ? 'Create Hook' : `Edit Hook '${name}'`">
        <div class="[ flex flex-col h-full gap-4 p-x-3 ]">
            <GridForm size="full">
                <GridFormField id="name" label="Name">
                    <template #input="{ id }">
                        <InputText :id="id" v-model="name" placeholder="Hook name" />
                    </template>
                </GridFormField>
                <GridFormField id="enabled" label="Enabled" v-if="selectedId !== -1">
                    <template #input="{ id }">
                        <ToggleSwitch :id="id" v-model="enabled" />
                    </template>
                </GridFormField>
                <GridFormField id="externalService" label="External Service">
                    <template #input="{ id }">
                        <ExternalServiceSelect :id="id" v-model="selectedExternalServiceId" />
                    </template>
                </GridFormField>
                <GridFormField id="events" label="Events">
                    <template #input="{ id }">
                        <AutoComplete :id="id" multiple fluid :suggestions="availableEvents" v-model="events"
                            @complete="updateSuggestions" dropdown></AutoComplete>
                    </template>
                </GridFormField>
            </GridForm>

            <div class="[ flex justify-end ]">
                <ButtonComponent :label="selectedId === -1 ? 'Create' : 'Update'" :loading="isUpdating || isCreating"
                    @click="commit" :disabled="!validForm" />
            </div>
        </div>
    </JBDrawer>
</template>

<script setup lang="ts">
import { Column, DataTable, Button as ButtonComponent, InputText, AutoComplete, Badge, ToggleSwitch, Checkbox, Select } from "primevue";
import { useApi, useApiLoader } from '@/services/api';
import { ExternalServiceConfig, Hook } from '@/services/api/sdk';
import { computed, onMounted, ref } from 'vue';
import JBDrawer from "@/components/ui/modals/JBDrawer.vue";
import GridForm from "@/components/ui/GridForm.vue";
import GridFormField from "@/components/ui/GridFormField.vue";
import JBDialog from "@/components/ui/modals/JBDialog.vue";
import Empty from "@/components/ui/Empty.vue";
import ExternalServiceSelect from "@/components/integrations/ExternalServiceSelect.vue";

defineOptions({
    inheritAttrs: false,
});

definePage({
    meta: { title: 'Hooks', icon: 'upload' },
});

// REFS

const showDrawer = ref(false);
const showConfirm = ref(false);
const selectedId = ref(-1);
const name = ref('');
const enabled = ref(true);
const events = ref<string[]>([]);
const selectedExternalServiceId = ref(-1);
const availableEvents = ref([
    "contact:created",
    "contact_participation:created",
    "contact:ended",
    "contact:summary_chunk_created",
    "utterance:created",
    "search:created",
    "customer_entry:created",
    "customer_note:created",
    "customer_entry:deleted",
    "customer_merge:created",
    "product_recommendation:requested",
    "command:execution_requested",
    "contact_event:created",
    "contact_event:deleted"
]);
const externalServices = ref<ExternalServiceConfig[]>([]);

// COMPUTED 

const validForm = computed(() => {
    return name.value;
});

// API

const api = useApi();

const { data, load, loading } = useApiLoader(async () => {
    const res = await api.listHooks();
    return res.data!;
}, []);

const { loading: isUpdating, load: updateHook } = useApiLoader(async () => {
    await api.updateHook({
        hookId: selectedId.value,
        updateHookRequest: {
            name: name.value,
            events: events.value,
            isEnabled: enabled.value,
            externalServiceId: selectedExternalServiceId.value,
        },
    });
}, null);

const { loading: isCreating, load: createHook } = useApiLoader(async () => {
    await api.createHook({
        createHookRequest: {
            name: name.value,
            events: events.value,
            externalServiceId: selectedExternalServiceId.value,
        },
    });
}, null);

const { loading: isDeleting, load: deleteHook } = useApiLoader(async () => {
    await api.deleteHook({
        hookId: selectedId.value,
    });
}, null);

// FUNCTION

function selectRow(data: Hook) {
    showDrawer.value = true;
    name.value = data.name || '';
    events.value = data.events || [];
    enabled.value = data.isEnabled || false;
    selectedExternalServiceId.value = data.externalService?.id!;
    selectedId.value = data.id!;
}

function openBlankDrawer() {
    showDrawer.value = true;
    name.value = '';
    events.value = [];
    enabled.value = true;
    selectedExternalServiceId.value = -1;
    selectedId.value = -1;
}

function confirmDelete(id: number) {
    selectedId.value = id;
    showConfirm.value = true;
}

async function commit() {
    const action = selectedId.value === -1 ? createHook : updateHook;
    if (await action()) {
        showDrawer.value = false;
        await load();
    }
}

async function commitDelete() {
    await deleteHook();
    showConfirm.value = false;
    await load();
}

function updateSuggestions() {
    availableEvents.value = [...availableEvents.value];
}

onMounted(async () => {
    load();
    const res = await api.getExternalServices();
    externalServices.value = res.data!;
});

</script>